/** Error body keys the services behind this console use, in priority order.
 *  @mgmt answers `{message}`; the Tiger controlplane answers `{error, message}`;
 *  the shared authz middleware and some vendor pass-throughs answer only
 *  `{error}` / `{detail}` / `{msg}`. Reading `message` alone left those failures
 *  with a bare title and no reason (e.g. a "Tenant context required" 403 shown as
 *  just "Issue failed"). */
const MESSAGE_KEYS = ['message', 'error', 'detail', 'msg'] as const;

/** Longest body text worth putting in a toast; anything longer is a payload
 *  dump, not a sentence. */
const MAX_MESSAGE_LEN = 300;

/** A trimmed, non-empty, toast-sized string — or undefined. */
function readable(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s || s.length > MAX_MESSAGE_LEN) return undefined;
  return s;
}

/**
 * Extract a human-readable message from an axios-style error.
 *
 * Falls through the known body shapes, then the HTTP status line, then the
 * transport error (e.g. "Network Error"), so a failed call always says
 * *something*. Returns undefined only when the value carries no message at all.
 */
export function errMessage(e: unknown): string | undefined {
  const err = e as {
    response?: { status?: number; statusText?: string; data?: unknown };
    message?: string;
  };
  const data = err?.response?.data;

  if (data && typeof data === 'object') {
    for (const key of MESSAGE_KEYS) {
      const msg = readable((data as Record<string, unknown>)[key]);
      if (msg) return msg;
    }
  }
  const asText = readable(data);
  if (asText) return asText;

  const status = err?.response?.status;
  if (status) {
    const statusText = readable(err.response?.statusText);
    return statusText ? `HTTP ${status} ${statusText}` : `HTTP ${status}`;
  }
  return readable(err?.message);
}

// Two-label public suffixes where the registrable apex is 3 labels
// (example.com.vn), not 2. Heuristic, not a full PSL — covers the suffixes we
// actually serve.
const MULTI_LABEL_SUFFIXES = new Set([
  'com.vn', 'net.vn', 'org.vn', 'edu.vn', 'gov.vn', 'biz.vn', 'info.vn', 'name.vn',
  'com.au', 'net.au', 'org.au', 'co.uk', 'org.uk', 'me.uk', 'co.jp', 'co.kr',
  'com.sg', 'com.my', 'co.id', 'com.cn', 'com.hk', 'com.tw', 'co.th', 'com.ph', 'com.br',
]);

/**
 * isApexDomain reports whether host is a registrable apex (example.com,
 * example.com.vn) rather than a subdomain (shop.example.com). For an apex we also
 * want the `*.host` wildcard so subdomains are covered; for a leaf host we
 * request only the host, since `*.leaf.example.com` would be useless.
 */
export function isApexDomain(host: string): boolean {
  const labels = host.split('.').filter(Boolean);
  if (labels.length < 2) return false;
  const lastTwo = labels.slice(-2).join('.');
  if (MULTI_LABEL_SUFFIXES.has(lastTwo)) return labels.length === 3;
  return labels.length === 2;
}

/**
 * The SAN set to issue for a WAAP domain: an apex gets `[host, *.host]` (cover
 * subdomains too), a leaf host gets just `[host]`. Shared by the onboarding
 * wizard and the website SSL tab so both issue identically.
 */
export function certDnsNamesFor(host: string): string[] {
  return isApexDomain(host) ? [host, '*.' + host] : [host];
}

/**
 * Does a single cert name entry (CN or SAN, possibly a `*.zone` wildcard) cover
 * host? Exact match, or a wildcard that matches exactly one label
 * (`*.example.com` covers `shop.example.com` but not `example.com` or
 * `a.b.example.com`). Tolerates a leading `DNS:` prefix.
 */
export function nameCoversHost(name: string, host: string): boolean {
  const n = name.trim().toLowerCase().replace(/^dns:/, '');
  const h = host.trim().toLowerCase();
  if (!n || !h) return false;
  if (n === h) return true;
  if (n.startsWith('*.')) {
    const suffix = n.slice(1); // ".example.com"
    if (!h.endsWith(suffix)) return false;
    const left = h.slice(0, h.length - suffix.length);
    return left.length > 0 && !left.includes('.');
  }
  return false;
}

/**
 * Does a WAAP vendor cert (its CN + comma/space-separated SAN list) cover host?
 * Used to filter the "Managed Certificate" picker to certs that can actually
 * attach to this domain.
 */
export function waapCertCoversHost(
  cert: { commonName: string; subjectAlternativeNames?: string },
  host: string,
): boolean {
  const names = [cert.commonName, ...(cert.subjectAlternativeNames ?? '').split(/[\s,;]+/)];
  return names.some((n) => nameCoversHost(n, host));
}
